import React from 'react';
import {connect} from 'react-redux';
import styled, { css } from 'styled-components';
import {Link} from 'react-router-dom';
import Sticky from 'react-stickynode';
import DrawerButton from "./DrawerButton";
import HeaderSearch from "./HeaderSearch";
import UserCinemaNav from './UserCinemaNav';
import { getMenuIcon } from '../utils/menuIcons';
import { getSelectedCinema, getUserInfo } from '../reducers';
import { getDesktopHeaderMenu, getDesktopHeaderSubMenu, getDesktopHeaderRightMenu, getMobileMenu } from '../reducers/menus';
import { asset } from '../utils/misc';
import {breakpoints} from '../utils/styles';
import DrawerNav from './DrawerNav';
import { logout } from '../actions/creators/auth';
import { authOpen } from '../actions/creators';
import IELogoH from '../assets/svg/IELogoH';
import { cinemaSelectorOpen } from '../actions/creators/ui';
import { PictureWithSources } from './common/PictureWithSources';

const Container = styled.div`
  body.no-nav & { display: none; }
`;

const Logo = styled(Link)`
  display: block;
  flex: auto;
  width: 140px;
  height: 30px;
  text-indent: -9999em;
  text-align: left;
  background-image: url("${props => asset('/dist/images/logo-cinemex' + (props.negative ? '-negative' : '') + '.svg?20250123')}");
  background-repeat: no-repeat;
  background-size: contain;
  margin-top: -0.3em;
  margin-right: 1rem;

  @media screen and (min-width: ${breakpoints.desktop}px) {
    flex: none;
    margin-top: -0.6em;
  }
  
  ${props => props.smNegative && `
    @media screen and (max-width: ${breakpoints.desktop - 1}px) {
      background-image: url("${asset('/dist/images/logo-cinemex-negative.svg?20250123')}");
    }
  `}
  
  body.theme-platinum & {
    background-image: url("${props => asset('/dist/images/logo-cinemex-platinum' + (props.negative ? '' : '') + '.svg?20250123')}");
    width: 204px;
  }
  
  body.theme-market & {
    background-image: url("${props => asset('/dist/images/logo-cinemex-market' + (props.negative ? '-negative' : '') + '.svg?20250123')}");
    margin-top: -0.7em;
    width: 204px;
  }
`;

const HeaderNav = styled.nav`
  background: var(--page-bg);
  padding: 1rem var(--pad-x);
  width: 100%;
  z-index: 1030;
  transition: background-color ease-in-out 0.5s;
  
  & > .container {
    padding: 0 !important;
    display: flex;
    align-items: center;
  }
  .nav-link { color: #fff !important; }
  @media screen and (min-width: ${breakpoints.desktop}px) { .nav-link { color: var(--primary) !important; } }

  ${props => props.fixed && 'box-shadow: 0 0 30px rgba(0,0,0,0.15);'}
  ${props => props.transparent && 'position: fixed; width: 100%;'}
  ${props => props.transparent && `
    background: transparent !important;
    
    .nav-link { color: #fff !important; }
    .navbar-toggler {
        color: rgba(255,255,255,0.5);
        border-color: rgba(255,255,255,0.1);
    }
  `}

  .c-nav__searchform {
    background: ${props => (props.transparent) ? 'black' : 'var(--tertiary)'};
  }

  ${props => props.baseStyle || ''}
`;

const Menu = styled.ul`
  display: none;
  font-weight: 400;
  list-style-type: none;
  margin-bottom: 0;
  margin-top: 0;
  padding: 0;
  position: relative;

  a {
    padding: .5rem 0;
  }
  &, a, span { color: var(--text-primary); }
  li { margin-bottom: 0; }
  a, span {
    display: inline-block; padding: .5rem; cursor: pointer; white-space: nowrap;
    &:hover { color: var(--primary); }
  }

  /* Hover filter for icons (applies in normal and transparent mode) */
  a:hover img, span:hover img {
    filter: brightness(0) saturate(100%) invert(19%) sepia(97%) saturate(7489%) hue-rotate(355deg) brightness(100%) contrast(117%);
  }

  ${props => props.transparent && `
    & > li > a, & > li > span { 
      color: #fff; 
      img { filter: brightness(0) invert(1); }
    }
  `}

  @media screen and (min-width: ${breakpoints.desktop}px) {
    display: flex;
  }
`;

const DropDownContent = styled.ul`
  list-style-type: none;
  padding: 0;
  border: 1px solid #bdc4c9;
  background: white;
  display: none;
  position: absolute;
  padding: 0.75rem 1.25rem 0.75rem 0.75rem ;
  box-shadow: 0 0 10px rgb(0 0 0 / 15%);
  border-radius: 0.5em;
  right: 1.5rem;
  text-align: center;
  top: 2.25em;
  transform: translateX(50%);

  &:before {
    border-right: 6px solid transparent;
    border-bottom: 6px solid white;
    border-left: 6px solid transparent;
    content: '';
    display: inline-block;
    position: absolute;
    right: 50%;
    top: -6px;
    transform: translateX(50%);
  }

  a { white-space: nowrap; padding: 0.25em; }
  a:hover { color: var(--primary) !important; }
  a i { display: inline-block; }

  body.theme-market & {
    a { color: #000; }
  }

  body.theme-platinum & {
    background: var(--text-primary);
    &:before { border-bottom-color: var(--text-primary); }
    a { color: #000; }
  }
`;

const DropDown = styled.li`
  position: relative;
  
  &:focus,
  &:hover {
    outline: none;
    & > .dropdown-menu {
      display: block; left: 100%; margin: 0; position: absolute; transform: translateX(-100%);
    }
  }
  
  .dropdown-item { padding: .25rem 1.5rem .25rem .75rem; }

  &:hover ${DropDownContent} { display: block; }
`;

const DropDownTrigger = styled.span`
  cursor: pointer;
  display: inline;

  &:after {
    display: inline-block;
    width: 0;
    height: 0;
    margin-left: .255em;
    vertical-align: .255em;
    content: "";
    border-top: .3em solid;
    border-right: .3em solid transparent;
    border-bottom: 0;
    border-left: .3em solid transparent;
  }
`;

const IELogo = styled.span`
  width: 4em;
  margin-top: -0.4em;
  margin-bottom: -1em;
  ${props => props.negative
    ? `
      fill: #fff
      &:hover { fill: var(--primary); }
    `
    : `
      fill: var(--text-primary);
      &:hover { fill: var(--primary); }
    `
  };
  
  body.theme-market &,
  body.theme-platinum & { margin-top: -0.5em; }
`;

const MenuIcon = styled(PictureWithSources)`
  display: inline-block;
  width: 1.5em;
  height: 1.5em;
  margin-right: 0.5em;
  vertical-align: middle;
  object-fit: contain;
`;

const RightMenuContainer = styled.div`
  margin-left: auto;
  display: none;
  align-items: center;

  ${MenuIcon} {
    margin-right: 0;
    margin-left: 0.5em;
  }

  ${props => props.transparent && css`
    ${MenuIcon} { filter: brightness(0) invert(1); }
  `}
  
  @media screen and (min-width: ${breakpoints.desktop}px) {
    display: flex;
  }
`;

class Header extends React.Component {
  constructor(props) {
    super(props);

    this.onMenuToggle = this.onMenuToggle.bind(this);

    this.state = {
      menuOpen: false,
    };
  }

  componentWillUnmount() {
    if (this.state.menuOpen) {
      document.body.classList.remove('overflow-h');
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.menuOpen && !prevState.menuOpen) {
      document.body.classList.add('overflow-h');
    } else if (!this.state.menuOpen && prevState.menuOpen) {
      document.body.classList.remove('overflow-h');
    }
  }

  onMenuToggle() {
    this.setState(state => ({menuOpen: !state.menuOpen}));
  }

  isInternalUrl(url) {
    return url && url.startsWith('/');
  }

  renderDynamicMenuItem(item, index, transparent) {
    const isInternal = this.isInternalUrl(item.url);
    const isLoopItem = item.url.includes('/loop/');
    const iconsSet = getMenuIcon(item.images || item.icons, false, this.props.cinema);

    const renderContent = () => {
      if (isLoopItem) {
        return <IELogo negative={transparent} area-label={item.title} dangerouslySetInnerHTML={{__html:IELogoH}}/>;
      }
      return (
        <React.Fragment>
          {!!iconsSet && Object.keys(iconsSet).length > 0 && <MenuIcon images={iconsSet} />}
          {item.title}
        </React.Fragment>
      );
    };
    
    return (
      <li key={index}>
        {isInternal ? (
          <Link to={item.url}>
            {renderContent()}
          </Link>
        ) : (
          <a href={item.url} target="_blank" rel="noopener noreferrer">
            {renderContent()}
          </a>
        )}
      </li>
    );
  }

  renderHeaderMenu(transparent) {
    const menuItems = this.props.headerMenu;
    
    if (menuItems && menuItems.length > 0) {
      return menuItems.map((item, index) => 
        this.renderDynamicMenuItem(item, index, transparent)
      );
    }

    return (
      <React.Fragment>
        <li><Link to="/">Inicio</Link></li>
        <li><Link to="/loop/programa"><IELogo negative={transparent} area-label="Cinemex Loop" dangerouslySetInnerHTML={{__html:IELogoH}}/></Link></li>
        <li><Link to="/landing/preventas/">Preventas</Link></li>
        <li><Link to="/landing/imax/peliculas">IMAX</Link></li>
        <li><Link to="/usuario/cupones">Cupones</Link></li>
      </React.Fragment>
    );
  }

  renderSubMenu() {
    const menuItems = this.props.headerSubMenu;

    if (!menuItems || menuItems.length < 1) {
      return null;
    }
    
    return (
      <DropDown>
        <DropDownTrigger>Más</DropDownTrigger>
        <DropDownContent>
          {menuItems.map((item, index) => 
            this.renderDynamicMenuItem(item, index, false)
          )}
        </DropDownContent>
      </DropDown>
    );
  }

  renderRightMenu(transparent) {
    const menuItems = this.props.headerRightMenu;

    if (!menuItems.length) {
      return null;
    }

    return menuItems.map((item, index) => {
      const linkStyle = { marginRight: '30px', color: transparent ? 'white' : 'var(--text-primary)' };
      const isInternal = this.isInternalUrl(item.url);
      const iconsSet = getMenuIcon(item.images || item.icons, false, this.props.cinema);

      const content = (
        <React.Fragment>
          {item.title}
          {!!iconsSet && Object.keys(iconsSet).length > 0 && <MenuIcon images={iconsSet} />}
        </React.Fragment>
      );

      return isInternal ? (
        <Link key={index} to={item.url} style={linkStyle}>
          {content}
        </Link>
      ) : (
        <a key={index} href={item.url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {content}
        </a>
      );
    });
  }

  render() {
    return <Container>
      <Sticky bottomBoundary="#the-body" innerZ="1031">{
        status => this.renderHeader({sticky: status.status})
      }</Sticky>

      <DrawerNav
        cinema={this.props.cinema}
        user={this.props.user}
        isOpen={this.state.menuOpen}
        onCloseClick={this.onMenuToggle}
        onLogInClick={this.props.authOpen}
        onLogOutClick={this.props.logout}
        onCinemaClick={this.props.openCinemaSelector}
        menuItems={this.props.mobileMenu}
      />
    </Container>;
  }

  renderHeader({sticky}) {
    const transparent = this.props.transparent || (this.props.feat && sticky !== Sticky.STATUS_FIXED);

    return <React.Fragment>
        <HeaderNav id="header" style={this.props.style} baseStyle={this.props.baseStyle}
          transparent={transparent} fixed={sticky === Sticky.STATUS_FIXED}>
          <div className="container no-width">
            <Logo negative={transparent} smNegative={this.props.logoSmNegative} to="/">Cinemex</Logo>
            {this.props.withUserCinemaNav && <UserCinemaNav negative={transparent} padding={false}/>}
            <DrawerButton onClick={this.onMenuToggle}/>
            <div>
              <Menu transparent={transparent}>
                {this.renderHeaderMenu(transparent)}
                {this.renderSubMenu()}
                {this.props.includeSearch && <HeaderSearch className="d-block d-sm-none"/>}
              </Menu>
            </div>

            <RightMenuContainer transparent={transparent}>
              {this.renderRightMenu(transparent)}
            </RightMenuContainer>
          </div>
        </HeaderNav>
    </React.Fragment>;
  }
}

const mapStateToProps = (state, props) => ({
  cinema: getSelectedCinema(state),
  feat: props.feat,// && !getHeaderSearchTerm(state), // @todo Rename this prop. When feat = true the header is transparent when page scroll is at top and becomes solid on scroll.
  user: getUserInfo(state),
  headerMenu: getDesktopHeaderMenu(state),
  headerSubMenu: getDesktopHeaderSubMenu(state),
  headerRightMenu: getDesktopHeaderRightMenu(state),
  mobileMenu: getMobileMenu(state)
});

const mapDispatchToProps = dispatch => ({
  authOpen: () => dispatch(authOpen()),
  logout: () => dispatch(logout()),
  openCinemaSelector: () => dispatch(cinemaSelectorOpen()),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(Header);